/**
 * Spalvinu Favourites — client-side favourites set shared across all tiers
 * (guest / demo / premium). Persisted in localStorage so it works for guests
 * who have no account. Stores an array of product handles under one key.
 *
 * Public API on window.SpalvinuFavourites:
 *   get()            -> string[]   list of favourited product handles
 *   has(handle)      -> boolean
 *   add(handle)      -> string[]
 *   remove(handle)   -> string[]
 *   toggle(handle)   -> boolean    new state (true = now favourited)
 *   rename(pairs)    -> string[]   {old: new} handle renames, one write
 *   setMissing(handles)            saved handles the favourites page got a 404 for
 *   count()          -> number
 *
 *   ready            -> Promise    settles once saved handles are migrated
 *
 * Dispatches `spalvinu:favourites-changed` on document after every mutation,
 * with detail { handles, count }.
 *
 * Handles are not permanent: on 2026-09-10 every set product moved from
 * `…-lod-N` to SEO-suffixed handles, and the old ones 404. Saved lists are
 * migrated once per browser through assets/favourites-handle-map.json
 * (old handle -> current handle, from the content pipeline's rename logs);
 * the layout passes its URL as this script's data-handle-map.
 *
 * So a saved handle that 404s is never pruned. The favourites page records it
 * under MISSING_KEY instead, and it stops counting toward the favourites pill
 * (layout/theme.liquid applies the same rule before first paint); a heart for
 * it showing up on any page proves it resolves again and clears it.
 */
(function () {
  var KEY = "spalvinu:favourites";
  var MISSING_KEY = "spalvinu:favourites-missing";
  var MIGRATED_KEY = "spalvinu:favourites-handles-2026-09-10";
  var scriptEl = document.currentScript;
  var MAP_URL = scriptEl && scriptEl.dataset ? scriptEl.dataset.handleMap : "";

  function readList(key) {
    try {
      var stored = localStorage.getItem(key);
      var parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (err) {
      return [];
    }
  }

  function read() {
    return readList(KEY);
  }

  function writeMissing(handles) {
    try {
      localStorage.setItem(MISSING_KEY, JSON.stringify(handles));
    } catch (err) {}
  }

  function write(handles) {
    try {
      localStorage.setItem(KEY, JSON.stringify(handles));
    } catch (err) {}
    document.dispatchEvent(
      new CustomEvent("spalvinu:favourites-changed", {
        detail: { handles: handles, count: handles.length }
      })
    );
    return handles;
  }

  var Favourites = {
    get: function () {
      return read();
    },
    has: function (handle) {
      return read().indexOf(handle) !== -1;
    },
    count: function () {
      return read().length;
    },
    add: function (handle) {
      if (!handle) return read();
      var handles = read();
      if (handles.indexOf(handle) === -1) {
        handles.push(handle);
        write(handles);
      }
      return handles;
    },
    remove: function (handle) {
      var handles = read().filter(function (h) {
        return h !== handle;
      });
      return write(handles);
    },
    // { oldHandle: newHandle, ... } in one write (one change event), keeping
    // each entry's position and dropping duplicates the renames create.
    rename: function (pairs) {
      var current = read();
      var handles = [];
      current.forEach(function (h) {
        var next = (pairs && pairs[h]) || h;
        if (handles.indexOf(next) === -1) handles.push(next);
      });
      if (handles.join("\n") === current.join("\n")) return current;
      return write(handles);
    },
    // Not a change to the favourites set, only to what the pill counts, so it
    // re-syncs the pill without a change event.
    setMissing: function (handles) {
      writeMissing((handles || []).filter(Boolean));
      syncOpeners();
    },
    toggle: function (handle) {
      if (!handle) return false;
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    }
  };

  // One-time rewrite of renamed handles, so hearts and the favourites page
  // match the products' current URLs. The flag is set only once the list is
  // known good (empty, or migrated), so a failed map fetch retries next load.
  function migrate() {
    var done = false;
    try {
      done = localStorage.getItem(MIGRATED_KEY) === "1";
    } catch (err) {
      return Promise.resolve();
    }
    var handles = read();
    if (done || !handles.length || !MAP_URL || !window.fetch) {
      if (!handles.length) {
        try {
          localStorage.setItem(MIGRATED_KEY, "1");
        } catch (err) {}
      }
      return Promise.resolve();
    }
    return fetch(MAP_URL)
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (map) {
        if (!map) return;
        Favourites.rename(map);
        try {
          localStorage.setItem(MIGRATED_KEY, "1");
        } catch (err) {}
      })
      .catch(function () {});
  }

  Favourites.ready = migrate();

  window.SpalvinuFavourites = Favourites;

  // ---- UI wiring ---------------------------------------------------------

  function handleFor(el) {
    if (!el) return "";
    if (el.dataset && el.dataset.productHandle) return el.dataset.productHandle;
    var wrapper = el.closest("[data-product-handle]");
    return wrapper ? wrapper.getAttribute("data-product-handle") : "";
  }

  function syncToggleButtons() {
    var set = read();
    var lookup = {};
    set.forEach(function (h) {
      lookup[h] = true;
    });
    var missing = readList(MISSING_KEY);
    var found = false;
    var buttons = document.querySelectorAll("[data-favourite-toggle]");
    Array.prototype.forEach.call(buttons, function (btn) {
      var handle = handleFor(btn);
      var active = !!(handle && lookup[handle]);
      btn.classList.toggle("is-favourited", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
      // A card on this page means its product resolves again.
      if (handle && missing.indexOf(handle) !== -1) {
        missing.splice(missing.indexOf(handle), 1);
        found = true;
      }
    });
    if (found) writeMissing(missing);
  }

  function syncOpeners() {
    // The favourites heart is in the DOM only while the list has a product
    // left (saved handles the favourites page found gone don't count). Each
    // bar keeps it in a <template data-favourites-pill> right where it goes
    // (snippets/spalvinu-category-filters.liquid). The <html> flag is what the
    // snippet reads to put it in as the page is parsed; layout/theme.liquid
    // sets it pre-paint to avoid a layout shift (CLS).
    var missing = readList(MISSING_KEY);
    var shown = read().some(function (h) {
      return missing.indexOf(h) === -1;
    });
    document.documentElement.classList.toggle("spalvinu-has-favourites", shown);
    var templates = document.querySelectorAll("template[data-favourites-pill]");
    Array.prototype.forEach.call(templates, function (tpl) {
      var pill = tpl.nextElementSibling;
      var present = !!(pill && pill.hasAttribute("data-favourites-open"));
      if (shown && !present) tpl.after(tpl.content.firstElementChild.cloneNode(true));
      if (!shown && present) pill.remove();
    });
  }

  function syncAll() {
    syncToggleButtons();
    syncOpeners();
  }

  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-favourite-toggle]");
    if (!btn) return;
    var handle = handleFor(btn);
    if (!handle) return;
    event.preventDefault();
    var nowFavourited = Favourites.toggle(handle);
    // Pop only when favouriting (not when removing), and only on this click —
    // never on the initial load sync.
    if (nowFavourited) {
      btn.classList.remove("is-fav-bounce");
      void btn.offsetWidth; // reflow so a rapid re-click restarts the animation
      btn.classList.add("is-fav-bounce");
      btn.addEventListener("animationend", function handler() {
        btn.classList.remove("is-fav-bounce");
        btn.removeEventListener("animationend", handler);
      });
    }
  });

  document.addEventListener("spalvinu:favourites-changed", syncAll);

  // Keep tabs in sync if favourites change in another tab/window.
  window.addEventListener("storage", function (event) {
    if (event.key === KEY || event.key === MISSING_KEY) syncAll();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncAll);
  } else {
    syncAll();
  }
})();
